"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  type EcommerceProduct
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function ProductSkeleton() {
  return (
    <Card className="overflow-hidden border-border bg-card shadow-sm rounded-lg">
      <div className="aspect-square w-full animate-pulse bg-muted" />
      <CardContent className="p-5">
        <div className="mb-2 h-5 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="mb-4 h-6 w-1/4 animate-pulse rounded-md bg-muted" />
        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-3 w-4/5 animate-pulse rounded-md bg-muted" />
        </div>
      </CardContent>
      <CardFooter className="p-5 pt-0">
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </CardFooter>
    </Card>
  );
}

function ShopPagination() {
  const { hasNext, hasPrev, nextPage, prevPage, currentPage, totalPages } = useEcommerceProducts();

  if (totalPages <= 1) return null;

  return (
    <Pagination className="mt-12">
      <PaginationContent>
        <PaginationItem>
          <Button
            variant="outline"
            size="default"
            onClick={prevPage}
            disabled={!hasPrev}
            className="border-border bg-background font-default text-foreground hover:bg-accent rounded-full"
          >
            Zurück
          </Button>
        </PaginationItem>
        <PaginationItem>
          <span className="px-4 text-sm font-medium text-muted-foreground font-default">
            Seite {currentPage} von {totalPages}
          </span>
        </PaginationItem>
        <PaginationItem>
          <Button
            variant="outline"
            size="default"
            onClick={nextPage}
            disabled={!hasNext}
            className="border-border bg-background font-default text-foreground hover:bg-accent rounded-full"
          >
            Weiter
          </Button>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

function ShopGridConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div data-section-id="222001" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <ProductSkeleton key={i} data-index={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center shadow-sm">
        <p className="mb-4 text-muted-foreground font-default">Produkte konnten nicht geladen werden.</p>
        <Button onClick={refetch} variant="outline" className="rounded-full">Erneut versuchen</Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center shadow-sm">
        <h3 className="mb-2 text-xl font-semibold text-foreground font-default">Keine Produkte gefunden</h3>
        <p className="text-muted-foreground font-default">Versuchen Sie es mit anderen Filtern oder Suchbegriffen.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product: EcommerceProduct, i: number) => (
          <Card key={product.id} data-index={i} className="group overflow-hidden border-border bg-card shadow-sm transition-all hover:shadow-lg rounded-lg">
            <Link to={product.link} className="relative block aspect-square overflow-hidden bg-muted/30">
              <div className="absolute left-3 top-3 z-10 flex flex-col gap-2">
                {product.customFields?.condition && (
                  <Badge variant="secondary" className="bg-background/90 font-default shadow-xs backdrop-blur-sm text-foreground">
                    {product.customFields.condition}
                  </Badge>
                )}
                {product.discounted_price && (
                  <Badge variant="default" className="bg-primary font-default text-primary-foreground shadow-xs">
                    Angebot
                  </Badge>
                )}
              </div>
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-sm text-muted-foreground font-default">
                  Kein Bild verfügbar
                </div>
              )}
            </Link>
            <CardContent className="p-5">
              <Link to={product.link}>
                <h3 className="line-clamp-2 text-lg font-semibold text-foreground transition-colors group-hover:text-primary font-default">
                  {product.title}
                </h3>
              </Link>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-xl font-bold text-foreground font-default">
                  €{(product.discounted_price || product.price || 0).toFixed(2)}
                </span>
                {product.discounted_price && product.price && (
                  <span className="text-sm text-muted-foreground line-through font-default">
                    €{product.price.toFixed(2)}
                  </span>
                )}
              </div>
              {product.short_description && (
                <div
                  className="mt-3 line-clamp-2 text-sm text-muted-foreground font-default [&>p]:mb-0"
                  dangerouslySetInnerHTML={{ __html: product.short_description }}
                />
              )}
            </CardContent>
            <CardFooter className="p-5 pt-0">
              <AddToCartButton
                productId={product.id}
                variant="default"
                className="w-full"
                buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-medium rounded-full transition-all shadow-md hover:shadow-lg"
                text="In den Warenkorb"
                showQuantity={false}
              />
            </CardFooter>
          </Card>
        ))}
      </div>
      <ShopPagination />
    </div>
  );
}

export default function ShopContent() {
  
  const current_wp_query_params =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="shop-content" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-8 md:mb-12">
          <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl font-default">
            Unsere Mehrwegbecher & Zubehör
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground font-default">
            Nachhaltige Lösungen für Ihr Event. Entdecken Sie unsere Auswahl an hochwertigen Bechern zum Kauf oder zur Miete.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
            {}
            <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-1/4">
              <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <h2 className="mb-6 text-lg font-semibold text-foreground font-default">Filter</h2>
                <div className="font-default">
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            {}
            <div className="flex w-full flex-col gap-6 lg:w-3/4">
              {}
              <div className="flex flex-col items-start justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center">
                <p className="text-sm font-medium text-muted-foreground font-default">
                  Produkte durchsuchen
                </p>
                <div className="w-full sm:w-auto font-default">
                  <ProductSortDropdown label="Sortieren nach" />
                </div>
              </div>

              {}
              <ShopGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}